// ============================================
// DOSSIERS RATTACHÉS PAR LE NOM DE DOMAINE
// ============================================
// Un e-mail d'un expéditeur sans compte (technicien@clim-dupont.fr) est relié
// par le tri à la société cliente dont un compte de la boutique utilise le
// même domaine d'entreprise (contact@clim-dupont.fr). Le serveur pose alors
// `rattachementDomaine` sur le dossier.
//
// CONTRAT avec le serveur (apps/backend/src/services/tickets/rattachement-domaine.ts) :
//   Ticket.rattachementDomaine = { domaine, societe, compteId, statut, … } ou null
//   - domaine  : « clim-dupont.fr » (avec ou sans « @ », casse indifférente) ;
//   - societe  : nom de la société sur la boutique, ou null ;
//   - customerCode : code client de cette société, ou null ;
//   - compteId : compte client du SAV proposé, ou null s'il n'y en a pas ;
//   - compteNom (facultatif) : nom de ce compte ;
//   - compteEmail (facultatif) : adresse de ce compte, montrée avant la
//                confirmation pour que le superviseur voie quel compte il relie.
//                Le serveur ne propose que le compte qui porte le code client
//                SAGE de la société, et le revérifie à la confirmation (409
//                sinon) ;
//   - statut   : 'PROPOSE' (stocké par le serveur ; 'A_CONFIRMER' accepté
//                comme synonyme) → à confirmer ; 'CONFIRME' → confirmé ;
//                'REFUSE' ou autre → ignoré (rien n'est affiché).
//
// Droits, tels que le serveur les applique (les deux routes sont ouvertes à
// tout le personnel, requireStaff) :
//   POST /admin/tickets/:id/rattachement-domaine/refuser   → tout le personnel ;
//   POST /admin/tickets/:id/rattachement-domaine/confirmer → tout le personnel,
//     SAUF quand la confirmation relie le dossier à un compte client (dossier
//     sans compte, compte proposé ou code client connu) : réservé à
//     l'encadrement (ADMIN, SUPERVISOR), comme le rattachement manuel
//     (POST /admin/tickets/:id/rattacher, décision GitLab #50). Le serveur
//     répond 403 à un conseiller dans ce cas. Comme le rattachement manuel
//     aussi, si l'adresse du dossier n'est pas celle du compte, le serveur
//     répond 409 avec le code ADRESSE_DIFFERENTE : les réponses partiront
//     vers l'adresse du compte, plus vers l'expéditeur. L'écran fait alors
//     confirmer, puis renvoie { confirmerAdresseDifferente: true }.
// L'écran suit la même règle : « Ce n'est pas cette société » pour tous,
// « Confirmer la société » pour l'encadrement, ou pour tous quand aucun
// compte ne sera relié. Une fois confirmé, il ne reste qu'une mention discrète.

import { AdminApi } from '../../services/api';
import { CODE_MOT_DE_PASSE_A_CHANGER } from '../auth/motDePasseObligatoire';
import type { RattachementDomaine } from '../../types';

/** Libellé du badge, dans la liste comme sur la fiche. */
export const LIBELLE_DOMAINE_A_CONFIRMER = 'Rattaché par le domaine — à confirmer';

/** Couleur informative (bleu clair du SAV), distincte du rouge « spam possible ». */
export const CLASSE_DOMAINE_A_CONFIRMER = 'bg-sapin-50 text-sapin-800 border border-sapin-200';

/** Statuts du serveur lus comme « à confirmer ». */
const STATUTS_A_CONFIRMER = new Set(['A_CONFIRMER', 'PROPOSE']);

const texte = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Lit la marque sans dépendre du type `Ticket` et la ramène à la forme de
 * l'écran (statut 'A_CONFIRMER' ou 'CONFIRME'). null si elle est absente,
 * refusée ou mal formée.
 */
export function lireRattachementDomaine(dossier: unknown): RattachementDomaine | null {
  if (typeof dossier !== 'object' || dossier === null) return null;
  const r = (dossier as { rattachementDomaine?: unknown }).rattachementDomaine;
  if (typeof r !== 'object' || r === null) return null;
  const brut = r as Record<string, unknown>;
  const domaine = texte(brut.domaine);
  if (!domaine) return null;
  const statut = typeof brut.statut === 'string' && STATUTS_A_CONFIRMER.has(brut.statut)
    ? 'A_CONFIRMER'
    : brut.statut === 'CONFIRME' ? 'CONFIRME' : null;
  if (!statut) return null;
  return {
    domaine,
    societe: texte(brut.societe),
    compteId: texte(brut.compteId),
    compteNom: texte(brut.compteNom),
    compteEmail: texte(brut.compteEmail),
    customerCode: texte(brut.customerCode),
    statut,
    origine: brut.origine === 'ADRESSE' ? 'ADRESSE' : 'DOMAINE',
    clientNom: texte(brut.clientNom),
  };
}

/** Le rattachement attend-il la confirmation du personnel ? */
export function domaineAConfirmer(dossier: unknown): boolean {
  return lireRattachementDomaine(dossier)?.statut === 'A_CONFIRMER';
}

/** Le rattachement a-t-il été confirmé ? */
export function domaineConfirme(dossier: unknown): boolean {
  return lireRattachementDomaine(dossier)?.statut === 'CONFIRME';
}

/**
 * Un dossier à confirmer qui n'est pas clos : badge et bandeau s'affichent.
 * Sur un dossier clos, plus personne ne tranche : ni badge, ni bandeau.
 */
export function decisionDomaineAttendue(dossier: unknown): boolean {
  return domaineAConfirmer(dossier) && (dossier as { status?: unknown }).status !== 'CLOSED';
}

/** « clim-dupont.fr », « @clim-dupont.fr » ou « CLIM-DUPONT.FR » → « @clim-dupont.fr ». */
export function domaineAffiche(domaine: string): string {
  return `@${domaine.trim().replace(/^@+/, '').toLowerCase()}`;
}

/**
 * Nom de la société du domaine : celui du rattachement, sinon celui du
 * compte proposé. Le nom du compte relié au dossier ne sert que si c'est
 * bien le compte proposé. null si aucun nom n'est connu.
 */
export function nomSociete(dossier: unknown, r: RattachementDomaine): string | null {
  const direct = texte(r.societe) ?? texte(r.compteNom) ?? texte(r.clientNom);
  if (direct) return direct;
  if (!r.compteId) return null;
  const d = (dossier ?? {}) as { customerId?: unknown; customer?: { id?: unknown; companyName?: unknown; displayName?: unknown } | null };
  const relie = d.customerId === r.compteId || (d.customer != null && d.customer.id === r.compteId);
  if (!relie || !d.customer) return null;
  return texte(d.customer.companyName) ?? texte(d.customer.displayName);
}

/** Adresse de l'expéditeur (première adresse de contact), ou null. */
export function adresseExpediteur(dossier: unknown): string | null {
  const brut = (dossier as { contactEmail?: unknown } | null)?.contactEmail;
  if (typeof brut !== 'string') return null;
  return brut.split(/[;,]/)[0]?.trim() || null;
}

/**
 * Ce que ferait la confirmation, d'après le dossier affiché (même logique
 * que confirmerRattachementDomaine côté serveur) :
 *  - 'relier'       : dossier sans compte, compte proposé ou code client
 *                     connu → le serveur reliera le dossier à ce compte
 *                     (encadrement seulement ; sans compte trouvé pour le
 *                     code client, il ne pose que le nom de la société) ;
 *  - 'nom-seul'     : dossier sans compte, ni compte proposé ni code client
 *                     → seul le nom de la société est posé (tout le personnel) ;
 *  - 'deja-relie'   : dossier déjà relié au compte proposé → rien n'est
 *                     relié, la société est seulement confirmée ;
 *  - 'a-verifier'   : dossier déjà relié à un compte, pas de compte proposé
 *                     mais un code client → le serveur n'accepte que si ce
 *                     code désigne le compte du dossier ;
 *  - 'autre-compte' : dossier déjà relié à un autre compte → le serveur
 *                     refuse la confirmation (409) ; reste le refus.
 */
export type SituationConfirmation = 'relier' | 'nom-seul' | 'deja-relie' | 'a-verifier' | 'autre-compte';

export function situationConfirmation(dossier: unknown, r: RattachementDomaine): SituationConfirmation {
  const d = (dossier ?? {}) as { customerId?: unknown };
  const relie = texte(d.customerId);
  // Client de la boutique reconnu par son adresse : le serveur crée ou
  // retrouve son compte SAV puis relie le dossier (encadrement seulement).
  if (estParAdresse(r)) return relie ? 'autre-compte' : 'relier';
  if (!relie) return r.compteId || r.customerCode ? 'relier' : 'nom-seul';
  if (r.compteId) return relie === r.compteId ? 'deja-relie' : 'autre-compte';
  return r.customerCode ? 'a-verifier' : 'autre-compte';
}

/** La confirmation est-elle réservée à l'encadrement (elle relierait un compte) ? */
export function confirmationReserveeEncadrement(dossier: unknown, r: RattachementDomaine): boolean {
  return situationConfirmation(dossier, r) === 'relier';
}

/** Le bouton « Confirmer la société » s'affiche-t-il pour cet utilisateur ? */
export function peutConfirmerDomaine(dossier: unknown, r: RattachementDomaine, encadrement: boolean): boolean {
  const s = situationConfirmation(dossier, r);
  if (s === 'autre-compte') return false;
  return s !== 'relier' || encadrement;
}

/** Proposition née de l'adresse exacte d'un compte de la boutique (22/10/2026). */
export function estParAdresse(r: RattachementDomaine | null | undefined): boolean {
  return r?.origine === 'ADRESSE';
}

/** Libellé du badge pour un client de la boutique reconnu par son adresse. */
export const LIBELLE_CLIENT_RECONNU = 'Client klygroupe.com reconnu — à rattacher';

/** Libellé du badge selon l'origine de la proposition. */
export function libelleBadgeDomaine(dossier: unknown): string {
  return estParAdresse(lireRattachementDomaine(dossier)) ? LIBELLE_CLIENT_RECONNU : LIBELLE_DOMAINE_A_CONFIRMER;
}

/** Titre du bandeau selon l'origine de la proposition. */
export function titreBandeauDomaine(r: RattachementDomaine): string {
  return estParAdresse(r) ? 'Client klygroupe.com reconnu' : 'Rattaché par le domaine — à confirmer';
}

/** Libellés des deux boutons selon l'origine de la proposition. */
export function libelleConfirmation(r: RattachementDomaine): string {
  return estParAdresse(r) ? 'Rattacher à ce client' : 'Confirmer la société';
}
export function libelleRefus(r: RattachementDomaine): string {
  return estParAdresse(r) ? 'Ce n\'est pas ce client' : 'Ce n\'est pas cette société';
}

/** Phrase d'explication du bandeau. */
export function explicationDomaine(dossier: unknown, r: RattachementDomaine): string {
  const expediteur = adresseExpediteur(dossier);
  const qui = expediteur ?? 'L\'expéditeur';
  if (estParAdresse(r)) {
    const nom = nomSociete(dossier, r);
    const titulaire = texte(r.clientNom);
    const precision = titulaire && titulaire !== nom ? ` (${titulaire})` : '';
    return `${qui} n'a pas encore de compte SAV, mais c'est l'adresse du compte klygroupe.com de ${nom ?? 'ce client'}${precision}.`;
  }
  const nom = nomSociete(dossier, r);
  const cible = nom ? `au client ${nom}` : 'à une société cliente';
  const deja = situationConfirmation(dossier, r) === 'deja-relie' ? ', déjà relié à ce dossier' : '';
  return `${qui} n'a pas de compte ; son domaine ${domaineAffiche(r.domaine)} correspond ${cible}${deja}.`;
}

/**
 * Destinataire des e-mails une fois le dossier relié : il ne change pas, les
 * réponses, avis et relances continuent de partir vers la personne qui a
 * écrit (serveur, `adresseDuClient` ; décision de Nathan du 14/10/2026).
 */
function changementDestinataire(dossier: unknown, _r: RattachementDomaine): string {
  const expediteur = adresseExpediteur(dossier);
  return expediteur ? ` Les réponses continueront de partir vers ${expediteur}.` : '';
}

/** Ce qu'entraîne la confirmation, dit avant le clic. */
export function consequenceConfirmation(dossier: unknown, r: RattachementDomaine): string {
  const nom = nomSociete(dossier, r);
  if (estParAdresse(r) && situationConfirmation(dossier, r) === 'relier') {
    return `En un clic, son compte SAV est créé (ou retrouvé) et le dossier y est rattaché : ${nom ?? 'ce client'} le verra dans son espace.${changementDestinataire(dossier, r)}`;
  }
  switch (situationConfirmation(dossier, r)) {
    case 'relier':
      if (r.compteId) {
        return (nom
          ? `Après confirmation, le client ${nom} verra ce dossier dans son espace.`
          : 'Après confirmation, le client de ce compte verra ce dossier dans son espace.') + changementDestinataire(dossier, r);
      }
      return `Après confirmation, le dossier sera relié au compte client de ${nom ?? 'cette société'} s'il en existe un : ce client le verra alors dans son espace.${changementDestinataire(dossier, { ...r, compteEmail: null })}`;
    case 'nom-seul':
      return `Après confirmation, le dossier sera rattaché à ${nom ?? 'cette société'}, sans compte client à relier.`;
    case 'deja-relie':
      return 'Le dossier est déjà relié à ce compte : la confirmation enregistre seulement la société.';
    case 'a-verifier':
      return 'Le dossier est déjà relié à un compte client : la confirmation ne sera acceptée que si c\'est celui de cette société.';
    case 'autre-compte':
      return 'Ce dossier est déjà rattaché à un autre compte client : la société ne peut pas être confirmée. Refusez la proposition si elle ne convient pas.';
  }
}

/**
 * Le compte client que la confirmation relierait, tel que le superviseur doit
 * le vérifier : « contact@clim-dupont.fr — code client C123 ». null quand la
 * confirmation ne relie pas un compte proposé (le serveur cherche alors le
 * compte par le code client ; la conséquence le dit déjà).
 */
export function compteARelier(dossier: unknown, r: RattachementDomaine): string | null {
  if (!r.compteId || situationConfirmation(dossier, r) !== 'relier') return null;
  const qui = texte(r.compteEmail) ?? texte(r.compteNom) ?? 'compte sans adresse connue';
  const code = texte(r.customerCode);
  return `Compte qui sera relié : ${qui}${code ? ` — code client ${code}` : ''}.`;
}

/** Mention discrète, une fois le rattachement confirmé. */
export function mentionDomaineConfirme(r: RattachementDomaine): string {
  if (estParAdresse(r)) return 'Client reconnu par l\'adresse de son compte klygroupe.com';
  return `Société identifiée par le domaine ${domaineAffiche(r.domaine)}`;
}

/** Phrase affichée au conseiller quand la confirmation relierait un compte client. */
export const AVIS_RESPONSABLE_DOMAINE = 'Un responsable doit confirmer ce rattachement, car il relie le dossier à un compte client.';

export type DecisionDomaine = 'confirmer' | 'refuser';

/** Réponse attendue des deux routes (champs facultatifs : on ne garde que ceux reçus). */
export interface ReponseDecisionDomaine {
  id?: string;
  rattachementDomaine?: unknown;
  customerId?: string | null;
  customer?: unknown;
  companyName?: string | null;
}

/**
 * Champs à fusionner dans le dossier affiché après une décision réussie.
 * Le serveur fait foi pour ce qu'il renvoie ; à défaut, la marque passe à
 * CONFIRME (confirmer) ou tombe (refuser).
 */
export function majApresDecision(
  decision: DecisionDomaine,
  avant: RattachementDomaine | null,
  reponse: ReponseDecisionDomaine | null | undefined,
): Record<string, unknown> {
  const r = (reponse && typeof reponse === 'object') ? reponse : {};
  const maj: Record<string, unknown> = {};
  if ('rattachementDomaine' in r) maj.rattachementDomaine = r.rattachementDomaine ?? null;
  else maj.rattachementDomaine = decision === 'confirmer' && avant ? { ...avant, statut: 'CONFIRME' } : null;
  for (const cle of ['customerId', 'customer', 'companyName'] as const) {
    if (cle in r) maj[cle] = r[cle] ?? null;
  }
  return maj;
}

/** Phrase de confirmation, après une décision réussie. */
export function avisDecisionDomaine(decision: DecisionDomaine, societe: string | null, parAdresse = false): string {
  if (parAdresse) {
    const client = societe ?? 'ce client';
    return decision === 'confirmer'
      ? `Dossier rattaché à ${client} : son compte SAV est prêt.`
      : `Rattachement refusé : le dossier n'est pas rattaché à ${client}. Rattachez-le au bon client si besoin.`;
  }
  const nom = societe ?? 'cette société';
  return decision === 'confirmer'
    ? `Société confirmée : le dossier est rattaché à ${nom}.`
    : `Rattachement refusé : le dossier n'est pas rattaché à ${nom}. Rattachez-le au bon client si besoin.`;
}

/** Message générique d'ApiError quand le serveur n'en donne pas. */
const MESSAGE_GENERIQUE = 'Une erreur est survenue';

/**
 * Message d'erreur lisible : celui du serveur s'il en donne un (403, 404,
 * 409…). Un 403 sans message traduit le refus réservé à l'encadrement ; le
 * 403 « mot de passe à changer » garde toujours son propre message.
 */
export function messageErreurDomaine(erreur: unknown): string {
  const e = erreur as { statusCode?: number; message?: string; code?: string } | null;
  const message = e && typeof e.message === 'string' && e.message.trim() && e.message !== MESSAGE_GENERIQUE ? e.message : null;
  if (e && e.statusCode === 403 && !message && e.code !== CODE_MOT_DE_PASSE_A_CHANGER) {
    return 'Seul un responsable peut confirmer ce rattachement.';
  }
  if (e && e.statusCode === 404 && !message) return 'Dossier introuvable.';
  return message ?? 'La décision n\'a pas pu être enregistrée.';
}

/**
 * Le serveur demande de confirmer : l'adresse du dossier n'est pas celle du
 * compte relié (409, code ADRESSE_DIFFERENTE), comme pour le rattachement
 * manuel (rattachement.ts, `demandeConfirmationAdresse`).
 */
export function demandeConfirmationAdresseDomaine(erreur: unknown): erreur is Error {
  const e = erreur as { statusCode?: unknown; code?: unknown } | null;
  return !!e && e.statusCode === 409 && e.code === 'ADRESSE_DIFFERENTE';
}

/**
 * Envoie la décision au serveur. Lève l'erreur de l'API.
 * `confirmerAdresseDifferente` : seulement après la demande du serveur.
 */
export async function trancherDomaine(
  ticketId: string,
  decision: DecisionDomaine,
  confirmerAdresseDifferente = false,
): Promise<ReponseDecisionDomaine> {
  if (decision === 'refuser') return AdminApi.refuserRattachementDomaine(ticketId);
  return confirmerAdresseDifferente
    ? AdminApi.confirmerRattachementDomaine(ticketId, true)
    : AdminApi.confirmerRattachementDomaine(ticketId);
}
